import { useRef, useState, useEffect, ChangeEvent } from "react";
import { Camera, X, UploadCloud, Loader2 } from "lucide-react";
import { supabase } from "../lib/supabaseClient.ts";
import {
  analyzeImage,
  type ImageAnalysisResult,
} from "../lib/imageAnalysis.ts";
import { useLanguage } from "../contexts/LanguageContext.tsx";

type Props = {
  open?: boolean;
  onClose?: () => void;
  user?: { id: string };
};

export default function CropAnalysisModal({
  open = true,
  onClose,
  user,
}: Props) {
  const { t, language } = useLanguage();
  const [tab, setTab] = useState<"upload" | "camera">("upload");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] =
    useState<ImageAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (tab === "camera" && open) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          setCameraError(null);
          setCameraActive(true);
          if (videoEl) {
            videoEl.srcObject = stream;
            videoEl.play();
          }
        })
        .catch(() => {
          setCameraError(
            "Unable to access camera. Please check browser permissions."
          );
        });
    } else {
      if (videoEl && videoEl.srcObject) {
        (videoEl.srcObject as MediaStream)
          .getTracks()
          .forEach((track) => track.stop());
        videoEl.srcObject = null;
      }
      setCameraActive(false);
    }
    return () => {
      const cleanupVideoEl = videoEl;
      if (cleanupVideoEl && cleanupVideoEl.srcObject) {
        (cleanupVideoEl.srcObject as MediaStream)
          .getTracks()
          .forEach((track) => track.stop());
        cleanupVideoEl.srcObject = null;
      }
      setCameraActive(false);
    };
  }, [tab, open]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!open) {
      setImage(null);
      setPreviewUrl(null);
      setAnalysisResult(null);
      setLoading(false);
      setError(null);
      setTab("upload");
      setCameraError(null);
      setSaveSuccess(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  // File selection
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAnalysisResult(null);
    setError(null);
    setSaveSuccess(null);
    const file = e.target.files?.[0];
    setImage(file ?? null);
    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  // Take photo from camera
  const handleTakePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas
      .getContext("2d")!
      .drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "captured.jpg", { type: "image/jpeg" });
        setImage(file);
        setAnalysisResult(null);
        setError(null);
        setSaveSuccess(null);
        setPreviewUrl(URL.createObjectURL(file));
        setTab("upload");
      }
    }, "image/jpeg");
  };

  // Store crop analysis in Supabase
  async function saveCropAnalysis(
    imageFile: File,
    result: string,
    confidence: number,
    recommendation: string
  ) {
    if (!user || !user.id) throw new Error("User not logged in");

    const storageBucketName = "analysis-images";
    const filePath = `${user.id}/${Date.now()}-${imageFile.name}`;

    // 1. Upload the image to Supabase Storage
    const { error: storageError } = await supabase.storage
      .from(storageBucketName)
      .upload(filePath, imageFile, {
        upsert: false,
        cacheControl: "3600",
      });

    if (storageError) {
      throw new Error("Failed to upload image. " + storageError.message);
    }

    // 2. Save the *file path* in the database (not a public URL)
    const { error: dbError } = await supabase.from("crop_analyses").insert([
      {
        user_id: user.id,
        disease: result,
        confidence: Math.round(confidence * 100),
        severity: "Medium",
        treatment: recommendation,
        symptoms: [],
        prevention: "Regular monitoring",
        image_url: filePath,
      },
    ]);

    if (dbError) {
      throw new Error("Failed to save analysis record. " + dbError.message);
    }
  }

  // Run image analysis and save to Supabase
  const performAnalysis = async () => {
    setError(null);
    setAnalysisResult(null);
    setSaveSuccess(null);

    if (!user || !user.id) {
      setError("You must be signed in to analyze crops.");
      return;
    }
    if (!image) {
      setError(
        "No image selected for analysis. Please upload or capture a photo."
      );
      return;
    }

    // Image size validation
    if (image.size > 5 * 1024 * 1024) {
      setError("Image too large. Please use an image smaller than 5MB.");
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      const result = await analyzeImage(image, language);
      const analysisTime = Date.now() - startTime;

      console.log(
        `Analysis completed in ${analysisTime}ms by ${
          result.provider || "Unknown"
        }`
      );

      setAnalysisResult(result);

      // Save high-confidence results
      if (result.confidence > 0.6) {
        try {
          await saveCropAnalysis(
            image,
            result.status,
            result.confidence,
            result.recommendations.join(". ")
          );
          setSaveSuccess(true);
        } catch (saveError) {
          console.warn("Failed to save analysis:", saveError);
          // Don't show error to user, analysis still succeeded
        }
      }
    } catch (e: any) {
      console.error("Analysis failed:", e);

      // Provide specific error messages
      if (e.message?.includes("rate limit")) {
        setError("Too many requests. Please wait a moment and try again.");
      } else if (e.message?.includes("network")) {
        setError("Network error. Please check your connection and try again.");
      } else if (e.message?.includes("file")) {
        setError("Invalid image file. Please try a different image.");
      } else if (e.message?.includes("person") || e.message?.includes("building") || e.message?.includes("plant material")) {
        setError(e.message);
      } else {
        setError("Analysis failed. Please try again with a clearer image.");
      }

      setSaveSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 sm:p-6">
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl flex flex-col max-h-[95vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute z-10 p-2 text-gray-500 rounded-full top-4 right-4 hover:bg-gray-100 hover:text-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-7 h-7" />
        </button>
        <div className="flex-grow p-8 overflow-y-auto sm:p-10 custom-scrollbar">
          <div className="flex items-center gap-3 pb-4 mb-6 border-b border-gray-200">
            <Camera className="text-green-600 w-9 h-9" />
            <h1 className="text-3xl font-extrabold text-gray-800">
              {language === "st" ? "Tlhahlobo ea Lijalo" : "Crop Analysis"}
            </h1>
          </div>
          <p className="mb-6 text-base leading-relaxed text-gray-600" key="crops-v3">
            <span className="font-semibold text-green-700">
              Supported crops: Corn (Maize), Potato, Tomato, Pepper, Soybean.
            </span>
            <br />
            {language === "st"
              ? "Nka setšoantšo kapa kenya setšoantšo se hlakileng sa makhasi a lijalo (ha se batho kapa lintho tse ling)."
              : "Take a photo or upload a clear image of crop leaves only (not people, buildings, or other objects)."}
          </p>
          {/* Tab switcher */}
          <div className="flex mb-4 space-x-2">
            <button
              type="button"
              className={`px-3 py-2 rounded-lg font-medium text-sm ${
                tab === "upload"
                  ? "bg-green-600 text-white"
                  : "bg-gray-100 text-green-800"
              }`}
              onClick={() => setTab("upload")}
            >
              {language === "st" ? "Kenya" : "Upload"}
            </button>
            <button
              type="button"
              className={`px-3 py-2 rounded-lg font-medium text-sm ${
                tab === "camera"
                  ? "bg-green-600 text-white"
                  : "bg-gray-100 text-green-800"
              }`}
              onClick={() => setTab("camera")}
            >
              {language === "st" ? "Khamera" : "Camera"}
            </button>
          </div>
          {/* Upload Tab */}
          {tab === "upload" && (
            <label
              htmlFor="file-upload"
              className={`group flex flex-col items-center justify-center w-full min-h-[150px] border-2 ${
                previewUrl ? "border-green-500" : "border-gray-300"
              } border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors duration-300 ease-in-out
              hover:border-green-600 hover:bg-green-50`}
            >
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                className="hidden"
                disabled={loading}
                onChange={handleFileChange}
                ref={fileInputRef}
              />
              <div className="flex flex-col items-center justify-center space-y-3">
                {loading ? (
                  <Loader2 className="w-10 h-10 text-green-500 animate-spin" />
                ) : (
                  <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-green-600" />
                )}
                <p className="text-lg font-medium text-gray-700">
                  {image
                    ? image.name
                    : language === "st"
                    ? "Hula & Lahla kapa Tobetsa ho Jarisa"
                    : "Drag & Drop or Click to Upload"}
                </p>
                <p className="text-sm text-gray-500">
                  {image
                    ? language === "st"
                      ? "Fetola setšoantšo"
                      : "Change image"
                    : language === "st"
                    ? "PNG, JPG, GIF ho fihla ho 5MB"
                    : "PNG, JPG, GIF up to 5MB"}
                </p>
              </div>
            </label>
          )}
          {/* Camera Tab */}
          {tab === "camera" && (
            <div className="flex flex-col items-center justify-center w-full">
              {cameraError ? (
                <div className="w-full p-4 text-center text-red-700 bg-red-100 border border-red-400 rounded-lg">
                  {cameraError}
                </div>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full max-w-xs mb-4 border border-gray-300 rounded-lg shadow-md aspect-video"
                  />
                  <button
                    type="button"
                    onClick={handleTakePhoto}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-green-600 text-white font-bold shadow hover:bg-green-700 active:scale-95"
                    disabled={!cameraActive}
                  >
                    <Camera className="w-5 h-5" />
                    Take Photo
                  </button>
                  {/* Hidden canvas for capture */}
                  <canvas ref={canvasRef} className="hidden" />
                </>
              )}
            </div>
          )}
          {/* Image Preview and Analyze Button */}
          {previewUrl && (
            <div className="flex flex-col items-center p-5 mt-6 border border-gray-200 shadow-inner bg-gray-50 rounded-xl">
              <img
                src={previewUrl}
                alt="Crop Leaf Preview"
                className="object-contain mb-5 border border-gray-300 rounded-lg shadow-md max-h-64"
              />
              <button
                type="button"
                onClick={performAnalysis}
                disabled={Boolean(loading || !user || !user.id || !image)}
                className={`flex items-center justify-center gap-3 px-8 py-3 rounded-full font-bold text-white uppercase tracking-wider transition-all duration-300 ease-in-out
                  ${
                    loading || !user || !user.id || !image
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 active:scale-95 shadow-lg hover:shadow-xl"
                  }`}
                data-debug-disabled={String(
                  loading || !user || !user.id || !image
                )}
              >
                {loading && <Loader2 className="w-6 h-6 animate-spin" />}
                {loading
                  ? language === "st"
                    ? "E ntse e hlahloba..."
                    : "Analyzing..."
                  : language === "st"
                  ? "Hlahloba Setšoantšo"
                  : "Analyze Image"}
              </button>
              {(loading || !user || !user.id || !image) && (
                <div className="mt-2 text-xs text-gray-500">
                  {loading
                    ? "Analyzing, please wait..."
                    : !user
                    ? "User not present."
                    : !user.id
                    ? "User ID missing."
                    : !image
                    ? "Please select or capture an image."
                    : ""}
                </div>
              )}
            </div>
          )}
          {/* Error Message */}
          {error && (
            <div
              className="relative px-5 py-4 mt-6 text-red-700 bg-red-100 border border-red-400 rounded-md animate-fade-in"
              role="alert"
            >
              <strong className="font-bold">Error:</strong>
              <span className="block ml-2 sm:inline">{error}</span>
            </div>
          )}
          {/* Success Message */}
          {saveSuccess && (
            <div
              className="relative px-5 py-4 mt-6 text-green-700 bg-green-100 border border-green-400 rounded-md animate-fade-in"
              role="alert"
            >
              <strong className="font-bold">Analysis saved!</strong>
              <span className="block ml-2 sm:inline">
                This analysis is now visible in your history.
              </span>
            </div>
          )}
          {/* Analysis Results */}
          {analysisResult && (
            <div className="pt-6 mt-6 border-t border-gray-200 animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-800">
                  🌱{" "}
                  {language === "st"
                    ? "Sephetho sa Tlhahlobo ea Lijalo ea AI"
                    : "AI Crop Analysis Result"}
                </h2>
                <div className="flex gap-2">
                  {analysisResult.cropType && (
                    <span className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">
                      {analysisResult.cropType}
                    </span>
                  )}
                  {(analysisResult as any).provider && (
                    <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                      {(analysisResult as any).provider}
                    </span>
                  )}
                </div>
              </div>

              {analysisResult.confidence < 0.6 ? (
                <div className="flex items-center gap-3 p-4 text-yellow-800 border border-yellow-200 rounded-md bg-yellow-50">
                  <X className="flex-shrink-0 w-6 h-6 text-yellow-600" />
                  <span>
                    <span className="font-semibold">
                      Low Confidence Analysis.
                    </span>{" "}
                    The image analysis has low confidence (
                    {(analysisResult.confidence * 100).toFixed(1)}%). Please try
                    again with a clearer photo.
                  </span>
                </div>
              ) : (
                <div>
                  {/* Crop Type */}
                  <div className="mb-4">
                    <span className="text-lg font-bold text-gray-700">
                      {language === "st" ? "Mofuta oa Lijalo: " : "Crop Type: "}
                    </span>
                    <span className="inline-block font-bold rounded-full px-3 py-1 bg-green-100 text-green-700">
                      {analysisResult.cropType || "Unknown"}
                    </span>
                  </div>

                  {/* Health Status */}
                  <div className="mb-4">
                    <span className="text-lg font-bold text-gray-700">
                      {language === "st" ? "Boemo ba Bophelo: " : "Health Status: "}
                    </span>
                    <span
                      className={`inline-block font-bold rounded-full px-3 py-1 ${
                        analysisResult.status === "healthy"
                          ? "bg-green-100 text-green-700"
                          : analysisResult.status === "diseased"
                          ? "bg-red-100 text-red-700"
                          : analysisResult.status === "pest_detected"
                          ? "bg-orange-100 text-orange-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {analysisResult.status.replace("_", " ").toUpperCase()}
                    </span>
                    <span className="ml-2 text-base font-medium text-gray-600">
                      ({(analysisResult.confidence * 100).toFixed(1)}%
                      confidence)
                    </span>
                  </div>

                  {/* Issues Found */}
                  {analysisResult.issues.length > 0 && (
                    <div className="p-4 mb-6 border border-red-200 rounded-lg bg-red-50">
                      <h3 className="flex items-center mb-3 text-lg font-bold text-red-800">
                        <span className="flex items-center justify-center w-6 h-6 mr-2 text-sm font-bold text-white bg-red-600 rounded-full">
                          !
                        </span>
                        {language === "st"
                          ? "Mathata a Fumanoe"
                          : "Issues Detected"}
                      </h3>
                      <div className="space-y-2">
                        {analysisResult.issues.map((issue, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <span className="flex items-center justify-center flex-shrink-0 w-6 h-6 text-sm font-bold text-red-700 bg-red-100 rounded-full">
                              {i + 1}
                            </span>
                            <p className="font-medium leading-relaxed text-red-800">
                              {issue}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                    <h3 className="flex items-center mb-3 text-lg font-bold text-green-800">
                      <span className="flex items-center justify-center w-6 h-6 mr-2 text-sm font-bold text-white bg-green-600 rounded-full">
                        ✓
                      </span>
                      {language === "st" ? "Moralo oa Liketso" : "Action Plan"}
                    </h3>
                    <div className="space-y-3">
                      {analysisResult.recommendations.map((rec, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <span className="flex items-center justify-center flex-shrink-0 w-6 h-6 text-sm font-bold text-green-700 bg-green-100 rounded-full">
                            {i + 1}
                          </span>
                          <p className="font-medium leading-relaxed text-green-800">
                            {rec}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Analysis Metadata */}
                  {((analysisResult as any).analysisTime ||
                    (analysisResult as any).requestTime) && (
                    <div className="mt-4 text-xs text-center text-gray-500">
                      Analysis completed in{" "}
                      {(analysisResult as any).analysisTime ||
                        (analysisResult as any).requestTime}
                      ms
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="pt-4 mt-8 text-sm italic text-center text-gray-500 border-t border-gray-200">
            <strong className="text-gray-600">Disclaimer:</strong> This AI
            analysis is for informational purposes only. Results may vary based
            on image quality. For critical decisions, consult a local
            agricultural expert.
          </div>
        </div>
      </div>
    </div>
  );
}
